import React, { useEffect, useState } from 'react';
import {
  CustomCollapseCard,
  CustomDatePicker,
  CustomForm,
  CustomFormItem,
  CustomInput,
  CustomSelect,
  errorDialog,
  Option,
  Text,
  useText,
} from '@src/components';
import { useSelector } from 'react-redux';
import { monthsList, orderTypeList } from '@src/store/enums';
import dayjs from '@src/utils/day';
import '@src/styles/cardOrder/addInformation.scss';

const AdditionalInformation = ({ form }) => {
  const { currentCardOrder } = useSelector((state) => state?.cardOrder);
  const [isTotalVisible, setIsTotalVisible] = useState(false);
  const [isDateControl, setIsDateControl] = useState(false);
  const currentDate = dayjs();
  const selectChoose = useText('choose');
  const eBillInformation = useText('enterYourAdditionalInformationRegardingEInvoice');
  const pickDateText = useText('pickDate');
  const orderTypeText = useText('orderType');
  const eInvoiceAdditionalInfoText = useText('eInvoiceAdditionalInfo');
  const balanceActivationDateRequired = useText('balanceActivationDateRequired');
  const youCannotEnterAPastDate = useText('youCannotEnterAPastDate');

  useEffect(() => {
    const initialValues = {
      orderType: currentCardOrder?.orderType,
    };

    const total = currentCardOrder?.totalPrice > 0;
    const currentDay = currentDate?.date();
    const currentMonth = currentDate?.month() + 1;
    const controlDate = currentDay >= 15;
    setIsDateControl(controlDate);
    setIsTotalVisible(total);
    const responseInvoiceMonth =
      currentCardOrder?.invoiceMonth !== 0 ? currentCardOrder?.invoiceMonth : undefined;
    const invoiceMonth = !controlDate ? currentMonth : responseInvoiceMonth || currentMonth;

    let nextM = currentMonth === 12 ? 1 : currentMonth + 1;
    let filterData = monthsList.filter((item) =>
      [currentMonth, nextM].includes(item.value),
    );
    if (filterData.length === 1) {
      setIsDateControl(false);
    }

    const activationDate = dayjs(currentCardOrder?.cardActivationDate);
    const isBefore = activationDate.isBefore(currentDate, 'day') || activationDate.isSame(currentDate, 'day');

    if (total) {
      initialValues.invoiceMonth = invoiceMonth;
      initialValues.cardActivationDate = isBefore ? null : activationDate;
      initialValues.eInvoiceInformation = currentCardOrder?.eInvoiceInformation;
    }
    form.setFieldsValue(initialValues);
  }, [currentCardOrder, currentDate, form, isDateControl, isTotalVisible]);

  const disabledDate = (current) => {
    return current && current < dayjs().startOf('day');
  };

  const mountSelectOptions = () => {
    const currentMonth = currentDate?.month() + 1;
    let nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;

    let filterData = monthsList.filter((item) =>
      [...new Set([currentCardOrder?.invoiceMonth, currentMonth, nextMonth])].includes(item.value),
    );

    if (!isDateControl) {
      filterData = monthsList.filter((item) => [currentMonth].includes(item.value));
    }
    if (currentMonth === 12) {
      filterData = filterData.reverse()
    }

    return filterData.map((month) => {
      return (
        <Option id={`ticketRestaurant-cardOrders-cardInformation-additionalInformation-invoiceMonth-${month?.text}-option`} key={month.value} value={month.value}>
          {month.text}
        </Option>
      );
    });
  };

  const pastDateValidator = async (_field, value) => {
    if (value !== null) {
      try {
        const now = new Date();
        const today = dayjs(now.toDateString())
        const selectedDate = dayjs(value);
        const isBefore = selectedDate.isBefore(today, 'day');
        if (isBefore) {
          errorDialog({
            title: <Text t="error" />,
            message: youCannotEnterAPastDate,
          });
          return Promise.reject(new Error('error'));
        }
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(new Error('error'));
      }
    } else {
      errorDialog({
        title: <Text t="error" />,
        message: balanceActivationDateRequired,
      });
      return Promise.resolve();
    }
  };

  const activationDateValidator = async (_field, value) => {
    if (value !== null) {
      try {
        const values = await form.getFieldsValue();
        let sysDateYear = new Date().getFullYear();
        let sysDateMonth = new Date().getMonth() + 1;

        if ((value.month() + 1) === values.invoiceMonth && value.year() === sysDateYear) {
          return Promise.resolve();
        }

        if (sysDateMonth === 12 && values.invoiceMonth === 1 && (value.month() + 1) === values.invoiceMonth && value.year() === sysDateYear + 1) {
          return Promise.resolve();
        }

        return Promise.reject(new Error('error'));
      } catch (e) {
        return Promise.reject(new Error('error'));
      }
    } else {
      return Promise.resolve();
    }
  };

  return (
    <>
      {currentCardOrder?.cardType !== 2 && (
        <CustomCollapseCard cardTitle={<Text t="additionalInformation" />}>
          <div className="add-information-container">
            <CustomForm
              name="additionalInformation"
              className="addInformationForm"
              initialValues={{}}
              autoComplete="off"
              layout={'horizontal'}
              form={form}
            >
              <div className="form-item">
                {isTotalVisible && (
                  <>
                    <CustomFormItem
                      label={<Text t="invoiceMonth" />}
                      name="invoiceMonth"
                      className={'bill-month-form-item'}
                      rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                    >
                      <CustomSelect
                        id="ticketRestaurant-cardOrders-cardInformation-additionalInformation-invoiceMonth-select"
                        placeholder={selectChoose}
                        disabled={!isDateControl}
                        style={{ width: 99 }}
                      >
                        {mountSelectOptions()}
                      </CustomSelect>
                    </CustomFormItem>

                    <CustomFormItem
                      label={<Text t="dateWhenBalanceWillBeAvailable" />}
                      name="cardActivationDate"
                      className="active-date-form-item"
                      rules={[{ required: true, message: <Text t="requiredField2" /> },
                      { validator: activationDateValidator, message: <Text t="balanceDateAndInvoiceMonthMustBeCompatible" /> },
                      { validator: pastDateValidator, message: <Text t="youCannotEnterAPastDate" /> }
                      ]}
                    >
                      <CustomDatePicker id="ticketRestaurant-cardOrders-cardInformation-additionalInformation-cardActivationDate-datePicker" placeholder={pickDateText} disabledDate={disabledDate} />
                    </CustomFormItem>
                  </>
                )}
                <CustomFormItem
                  label={orderTypeText}
                  name="orderType"
                  className={'order-type-form-item'}
                >
                  <CustomSelect id="ticketRestaurant-cardOrders-cardInformation-additionalInformation-orderType-select" placeholder={selectChoose} style={{ width: 160 }}>
                    {orderTypeList.map((orderType) => (
                      <Option id={`ticketRestaurant-cardOrders-cardInformation-additionalInformation-orderType-${orderType?.text}-option`} key={orderType.value} value={orderType.value}>
                        {orderType.text}
                      </Option>
                    ))}
                  </CustomSelect>
                </CustomFormItem>
              </div>
              {isTotalVisible && (
                <div className="form-item">
                  <CustomFormItem
                    label={eInvoiceAdditionalInfoText}
                    name="eInvoiceInformation"
                    className={'delivery-address-add-form-item'}
                  >
                    <CustomInput id="ticketRestaurant-cardOrders-cardInformation-additionalInformation-eInvoiceInformation-input" placeholder={eBillInformation} maxLength={150} hasNumberChar={true} />
                  </CustomFormItem>
                  <CustomFormItem label={<Text t="invoiceAddress2" />} className={'delivery-address-form-item'}>
                    <span id="ticketRestaurant-cardOrders-cardInformation-additionalInformation-deliveryAddress-span" className="address-info-box">{currentCardOrder?.invoiceAddress}</span>
                  </CustomFormItem>
                </div>
              )}
            </CustomForm>
          </div>
        </CustomCollapseCard>
      )}
    </>
  );
};

export default AdditionalInformation;
